import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAppContext } from "../utils/context/app-context";
import {
	StyledRadioButtons,
	FormBeforeAfterContent,
} from "../components/FormComponents";
import {
	Button,
	ContentContainer,
	TitleHeader,
	NarrowContentWrapper,
	AlertMessage,
} from "../components/UiComponents";
import { CourseHeader, CourseWrapper } from "../components/CourseComponents";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";

export const questions = {
	punctuality: null,
	attitude: null,
	behaviour: null,
	team_player: null,
	breakaway_techniques: null,
	pmva_techniques: null,
	escorting_holds: null,
	teamwork: null,
	seated_deescalation: null,
	role_of_head_person: null,
	floor_work: null,
	seclusion: null,
};

const PhysicalCompetencyPage = () => {
	const { dispatch, api, state } = useAppContext();
	useDocumentTitle("Physical Competency Assessment");

	const { studentId } = useParams();
	const [loading, setLoading] = useState(true);
	const [validationErrors, setValidationErrors] = useState(null);
	const [formData, setFormData] = useState(questions);
	const [comments, setComments] = useState("");
	// eslint-disable-next-line no-unused-vars
	const [error, setError] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const [submitted, setSubmitted] = useState(false);
	const student = api.findStudentById(studentId);
	let course = api.getCurrentCourse();
	let date = api.getCurrentDate();
	const extraContent =
		state?.forms_extra_content["physical_competency_assessment"] || {};
	const isReadOnly = useUserIsReadOnly();

	const passReferOptions = [
		{ value: 1, label: "Pass" },
		{ value: 2, label: "Refer" },
	];

	React.useEffect(() => {
		fetchData();
	}, []);

	async function fetchData() {
		if (state.offline) {
			// Find offline form data if any exists
			setLoading(false);
			return;
		}

		const { data, error } = await api.fetchData(
			`/forms/physical-competency-form/${studentId}/${course.id}`,
		);
		if (data?.form) {
			const loadedAnswers = data.form;
			const newFormData = { ...questions };

			for (const key of Object.keys(questions)) {
				// The backend returns { punctuality: 1, attitude: 2, ... } as the form directly
				if (loadedAnswers[key] !== undefined && loadedAnswers[key] !== null) {
					newFormData[key] = parseInt(loadedAnswers[key]);
				} else {
					// Default all other options to pass (1)
					newFormData[key] = 1;
				}
			}
			setFormData(newFormData);
		}
		setLoading(false);
	}

	function handleFieldChanged(name, value) {
		setFormData({
			...formData,
			[name]: value,
		});
	}

	const PassReferField = ({ name, label }) => {
		function findValue() {
			// Check if formData has a value for this field
			if (formData[name]) {
				return formData[name];
			}

			return null;
		}

		function findValidationError() {
			// Check if validationErrors has an error for this field
			if (validationErrors && validationErrors[name]) {
				return validationErrors[name];
			}

			return null;
		}

		return (
			<StyledRadioButtons
				label={label}
				name={name}
				options={passReferOptions}
				value={findValue(name)}
				onChange={(val) => {
					handleFieldChanged(name, val);
				}}
				isPassFail={true}
				required={true}
				error={findValidationError(name)}
			/>
		);
	};

	async function handleSubmit(event) {
		event.preventDefault();
		setValidationErrors(null);
		setSubmitted(false);
		setError(null);
		setSubmitting(true);

		// All fields in formData must be either 1 or 2 except for comments
		let errors = {};

		for (const [key, value] of Object.entries(formData)) {
			if (value != 1 && value != 2 && key !== "comments") {
				errors[key] = "This field is required";
			}
		}

		if (Object.keys(errors).length) {
			setValidationErrors(errors);
			return;
		}

		const dataToPost = {
			course_id: course.id,
			form_data: {
				comments: comments,
				students: {
					[studentId]: formData,
				},
			},
		};

		const { data, error } = await api.fetchData(
			`/forms/physical-competency-form`,
			"POST",
			dataToPost,
		);

		if (data?.success) {
			dispatch({
				type: "SET_STUDENT_PHYSICAL_COMPETENCY",
				payload: {
					student_id: student.student_id,
					course_id: course.id,
					date_id: date.id,
					val: 1,
					result: hasFailAnswers() ? 0 : 1,
				},
			});
			setSubmitting(false);
			setSubmitted(true);
		} else {
			setError(error || data?.error || "An error occurred");
			setSubmitting(false);
		}
	}

	function hasFailAnswers() {
		// eslint-disable-next-line no-unused-vars
		for (const [key, value] of Object.entries(formData)) {
			if (value == 2) {
				return true;
			}
		}

		return false;
	}

	return (
		<div className="physical-competency-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title="Physical Competency Assessment" />
					<NarrowContentWrapper>
						{submitted && (
							<div className="physical-competency-page__submitted">
								<h3 className="physical-competency-page__submitted__title">
									Thank you
								</h3>
								<div className="physical-competency-page__submitted__message">
									<p>Your assessment has been submitted.</p>
									{hasFailAnswers() && (
										<div className="physical-competency__refer-message">
											<p>
												As you have answered "Refer" to one or more of the
												questions, you will need to complete a Referral
												Competency Form.
											</p>
											<Link
												className="btn btn-primary"
												to={`/courses/${course.id}/refer/${studentId}`}
											>
												Complete Referral Competency Form
											</Link>
										</div>
									)}
								</div>
							</div>
						)}
						{loading ? (
							<div className="loading-container">
								Loading Assessment Data...
							</div>
						) : (
							student &&
							!submitted && (
								<>
									{student.has_physical_competency === 1 && (
										<div className="mb-4">
											<AlertMessage
												type="danger"
												message="This Participant has already completed the Physical Competency Assessment. Submitting another one will overwrite the already submitted form"
											/>
										</div>
									)}
									<FormBeforeAfterContent
										content={extraContent?.before ?? null}
									/>
									<div className="student-info-header">
										<div className="student-info-header__name">
											{student.first_name} {student.last_name}
										</div>
										<div className="student-info-header__email">
											{student.email}
										</div>
									</div>
									<form
										action=""
										className="form physical-competency-form"
										onSubmit={handleSubmit}
										noValidate
									>
										<PassReferField name="punctuality" label="Punctuality" />
										<PassReferField name="attitude" label="Attitude" />
										<PassReferField
											name="behaviour"
											label="Behavious / Language"
										/>
										<PassReferField name="team_player" label="Team Player" />
										<PassReferField
											name="breakaway_techniques"
											label="Breakaway Techniques"
										/>
										<PassReferField
											name="pmva_techniques"
											label="PMVA Techniques"
										/>
										<PassReferField
											name="escorting_holds"
											label="Escorting Holds"
										/>
										<PassReferField name="teamwork" label="Teamwork" />
										<PassReferField
											name="seated_deescalation"
											label="Seated Deescalation"
										/>
										<PassReferField
											name="role_of_head_person"
											label="Role of Head Person"
										/>
										<PassReferField name="floor_work" label="Floor Work" />
										<PassReferField name="seclusion" label="Seclusion" />
										{validationErrors && (
											<div className="mb-3">
												<AlertMessage
													type="danger"
													message="Please check the form for errors and try again."
												/>
											</div>
										)}
										<Button
											text="Submit Details"
											type="primary"
											buttonType="submit"
											disabled={submitting || isReadOnly}
										/>
									</form>
									<FormBeforeAfterContent
										content={extraContent?.after ?? null}
										type="after"
									/>
								</>
							)
						)}
						{!student && (
							<AlertMessage type="danger" message="Participant not found" />
						)}
					</NarrowContentWrapper>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

export default PhysicalCompetencyPage;
